<template>
    <wizard
        title="Manual Submission Tracker"
        :steps="steps"
        :current-step="currentStep"
        :loading="loading"
    >
        <x-form ref="mainForm" class="wizard-step-form">
            <x-container fluid>
                <template v-if="currentStep === 1">
                    <m-s-t-entry-client-information
                        :submission="submission"
                        :companies="companies"
                        :facilities="facilities"
                        :entities="entities"
                        :categories="categories"
                        :products="products"
                        :countries="countries"
                        :obligors="obligors"
                        :currencies="currencies"
                        :business-lines="businessLines"
                        :client-assignments="clientAssignments"
                        @update:isKnownFacility="setIsKnownFacility"
                    />
                </template>

                <template v-else-if="currentStep === 2">
                    <m-s-t-entry-insurer-information
                        :submission="submission"
                        :insurerCompanies="insurerCompanies"
                        :currencies="currencies"
                        :securities="securities"
                    />
                </template>

                <x-layout class="action-set" align-center>
                    <x-flex xs6 offset-xs3 class="action-set-actions">
                        <x-container>
                            <x-layout>
                                <x-flex xs6>
                                    <x-btn
                                        v-if="currentStep === 1"
                                        color="tertiary"
                                        @click="cancelSubmission()"
                                    >
                                        <i class="material-icons mr-2">arrow_back</i>

                                        Cancel
                                    </x-btn>

                                    <x-btn
                                        v-if="currentStep === 2"
                                        color="tertiary"
                                        @click="back()"
                                    >
                                        <i class="material-icons mr-2">arrow_back</i>

                                        Back
                                    </x-btn>
                                </x-flex>

                                <x-flex xs6 text-xs-right>
                                    <x-btn
                                        class="wide"
                                        color="primary"
                                        :disabled="loading"
                                        data-test="next"
                                        @click="submit"
                                    >
                                        {{ this.currentStep === 1 ? 'Next' : 'Save'}}

                                        <i class="material-icons ml-2">trending_flat</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </x-container>
                    </x-flex>
                </x-layout>
            </x-container>
        </x-form>

        <modal
            ref="unsavedModal"
            title="Return to dashboard?"
            confirmation
        >
            <p>Are you sure you want to go to dashboard? (progress will be lost)</p>
        </modal>

        <modal
            ref="errorModal"
            title="Unable to save"
            max-width="620"
        >
            <ul class="error--text">
                <li v-for="(message, index) in errorMessages" :key="`save-error-${index}`">
                    {{ message }}
                </li>
            </ul>
        </modal>
    </wizard>
</template>

<script>
import Modal from "../../../../components/Shared/UIComponents/Modal.vue";
import MSTEntryClientInformation
    from "../../../../components/Broker/ManualSubmissionTracker/MSTEntryClientInformation.vue";
import MSTEntryInsurerInformation
    from "../../../../components/Broker/ManualSubmissionTracker/MSTEntryInsurerInformation.vue";
import generalHelpers from "../../../../mixins/generalHelpers";
import _ from "lodash";
import Wizard from "../../../../components/Broker/UIComponents/Wizard.vue";

export default {
    name: 'BrokerManualSubmissionTrackerEntry',
    components: {
        Wizard,
        MSTEntryInsurerInformation,
        MSTEntryClientInformation,
        Modal
    },

    mixins: [generalHelpers],

    data() {
        return {
            loading: true,
            currentStep: 1,
            steps: [
                'Client Details',
                'Insurer Details',
            ],
            submission: {},
            companies: [],
            facilities: [],
            entities: [],
            products: [],
            currencies: [],
            categories: [],
            countries: [],
            obligors: [],
            securities: [],
            businessLines: [],
            clientAssignments: [],
            errorMessages: [],
        }
    },
    mounted() {
        this.$eventBus.$emit('componentWillHandleErrorCodes', {
            componentID: this._uid,
            codesArray: [422]
        });

        this.submission = this.getEmptySubmission();

        const mountRequests = [];

        mountRequests.push(axios.get(this.$root.api('company')).then(res => {
            this.companies = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('facility')).then(res => {
            this.facilities = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('entity')).then(res => {
            this.entities = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('product')).then(res => {
            this.products = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('currency')).then(res => {
            this.currencies = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('categories')).then(res => {
            this.categories = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('country')).then(res => {
            this.countries = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('obligor')).then(res => {
            this.obligors = res.data.data
        }));
        mountRequests.push(axios.get(this.$root.api('broker/security')).then(res => {
            this.securities = res.data.data;
        }));
        mountRequests.push(axios.get(this.$root.api('business-lines')).then(res => {
            this.businessLines = res.data.data;
        }));
        mountRequests.push(axios.get(this.$root.api('broker/client-assignment/placing-brokers')).then(res => {
            this.clientAssignments = res.data;
        }));

        if (this.$route.name === 'broker-edit-mst' && this.$route.params.id) {
            mountRequests.push(axios.get(this.$root.api(`broker/manual-submission-tracker/${this.$route.params.id}`)).then(res => {
                this.submission = this.packSubmissionForFrontend(res.data.data);
            }));
        }

        axios.all(mountRequests).then(() => {
            this.loading = false;
        });
    },
    beforeDestroy() {
        this.$eventBus.$emit('clearComponentWillHandleErrorCodes', this._uid);
    },
    methods: {
        async cancelSubmission() {
            if (await this.$refs.unsavedModal.open() === false) {
                return;
            }

            await this.$router.push('/agent/dashboard');
        },
        setIsKnownFacility(value) {
            this.submission.is_known_facility = value;
        },
        submit() {
            if (this.$refs.mainForm.validate()) {
                switch (this.currentStep) {
                    case 1:
                        this.currentStep++;
                        break;
                    case 2:
                        this.loading = true;
                        this.saveSubmission()
                            .then(() => this.$router.push({path: `/agent/manual-submission/${this.submission.id}`}))
                            .catch(error => this.handleSaveError(error))
                            .finally(() => {
                                this.loading = false;
                            });

                        break;
                }
            }
        },
        saveSubmission() {
            const payload = this.packSubmissionForBackend();

            if (this.$route.name === 'broker-edit-mst') {
                return axios.put(this.$root.api(`broker/manual-submission-tracker/${this.submission.id}`), payload);
            }

            return axios.post(this.$root.api('broker/manual-submission-tracker'), payload).then(res => {
                this.submission = res.data.data;
            });
        },
        handleSaveError(error) {
            if (error.response?.status !== 422) {
                return;
            }

            this.errorMessages = Object.values(error.response.data?.errors ?? {}).flat(Infinity);
            this.$refs.errorModal.open();
        },
        back() {
            this.currentStep--;
        },
        packSubmissionForBackend() {
            let submission = _.cloneDeep(this.submission);

            submission.placing_broker_company_id = this.$root.user.company_id;
            if (submission.is_known_facility === false) {
                submission.facility_id = null;
            }

            let loanCategory = this.categories.find(category => category.name === 'Loans');
            if (submission.category_id !== loanCategory?.id) {
                submission.loan_radar_id = null;
            }

            return submission;
        },
        packSubmissionForFrontend(submission) {
            submission.anticipated_bind_date = submission.anticipated_bind_date
                ? this.$moment(submission.anticipated_bind_date).format('YYYY-MM-DD') : null;
            submission.date_enquiry_received = submission.date_enquiry_received
                ? this.$moment(submission.date_enquiry_received).format('YYYY-MM-DD') : null;
            submission.entities = this.pluck(submission.entities, 'id');
            return submission;
        },
        getEmptySubmission() {
            return {
                obligors: [],
                guarantors: [],
                insurers: [],
                total_risk_period: null,
                status: 'draft',
                probability_of_binding: null,
            };
        },
    },
    computed: {
        insurerCompanies() {
            if (this.selectedFacility) {
                //filter insurer companies based on facility
                const facilityInsurerCompanyIds = this.pluck(this.selectedFacility.insurer_companies, 'id');
                return this.companies.filter(company => facilityInsurerCompanyIds.includes(company.id));
            }
            return this.companies.filter(company => company.company_type === 'insurer');
        },
        selectedFacility() {
            if (this.submission.facility_id) {
                return (this.facilities.find(fac => fac.id === this.submission.facility_id));
            }
            return null;
        }
    },
    watch: {
        '$route.name'(newVal, oldVal) {
            if (newVal !== oldVal && newVal === 'broker-new-mst') {
                this.submission = this.getEmptySubmission();
                this.currentStep = 1;
            }
        }
    }
}
</script>

<style scoped lang="scss">
h1 {
    padding-top: rem(6px);
    color: $color-grey-7;
    font-size: rem(89px);
    letter-spacing: rem(-3.5px);
    line-height: 0.8;
}

.wizard-side {
    flex-basis: 45%;
}
.wizard-container {
    width: 100%;
    max-width: 585px;

    &.submission-complete {
        max-width: none;
    }

    &.manual-firm-order {
        max-width: none;
    }

    .layout.hide {
        opacity: 0;
    }

    .wizard-heading-container {
        margin-bottom: rem(45px);

        .wizard-heading {
            display: flex;
            align-items: baseline;
            flex-grow: 0;
            color: $color-grey-7;
            font-family: $font-title;
            font-size: rem(21px);
            letter-spacing: rem(-0.8px);
            line-height: 0.9em;
            transition: color $transition-length;

            &.active {
                color: $color-white;
            }

            .wizard-heading-step {
                font-size: rem(77px);
                padding-right: rem(8px);
            }

            .wizard-heading-text {
                position: relative;
                top: -0.9em; // we move this up by an amount equal to the 'line-height', as '.wizard-heading' aligns items on their baseline, but we want it aligned to the second line's baseline
            }
        }
    }

    .step-form {
        padding: 32px;
    }
    .cancel-cta {
        color: inherit;
        font-family: $font-title;
        font-size: rem(12px);

        &:hover {
            text-decoration: underline;
        }
    }
}
</style>
